import { escapeHtml } from "./formatters.js";

const SAFE_CLASSNAME_RE = /^[a-zA-Z0-9:_\-\s]+$/u;
const SAFE_CSS_VALUE_RE = /^[-#(),.%/\sa-zA-Z0-9]+$/u;
const SAFE_CSS_PROPERTY_RE = /^(?:--[a-zA-Z0-9-]+|[a-zA-Z][a-zA-Z0-9-]*)$/u;
const SAFE_ATTR_VALUE_RE = /^[^\u0000"'<>`]+$/u;
const SAFE_HTML_ATTR_NAME_RE = /^(?:data|aria)-[a-zA-Z0-9_.:-]+$/u;

export function safeRendererClassName(value, fallback = "") {
  const safeValue = String(value || "").trim();
  if (!safeValue) {
    return fallback;
  }
  return SAFE_CLASSNAME_RE.test(safeValue) ? safeValue : fallback;
}

export function safeRendererClassList(values = [], fallback = "") {
  const safeTokens = (Array.isArray(values) ? values : [values])
    .flatMap((value) => String(value || "").split(/\s+/u))
    .map((token) => safeRendererClassName(token, ""))
    .filter(Boolean);

  return safeTokens.length > 0 ? safeTokens.join(" ") : fallback;
}

export function safeRendererStyleValue(value, fallback = "") {
  const safeValue = String(value || "").trim();
  if (!safeValue) {
    return fallback;
  }
  return SAFE_CSS_VALUE_RE.test(safeValue) ? safeValue : fallback;
}

export function safeRendererStyleDeclaration(propertyName, value, fallback = "") {
  const safePropertyName = String(propertyName || "").trim();
  if (!SAFE_CSS_PROPERTY_RE.test(safePropertyName)) {
    return fallback;
  }

  const safeValue = safeRendererStyleValue(value, "");
  if (!safeValue) {
    return fallback;
  }

  return `${safePropertyName}:${safeValue}`;
}

export function safeRendererStyleDeclarations(declarations = [], fallback = "") {
  const safeDeclarations = (Array.isArray(declarations) ? declarations : [])
    .map(([propertyName, value]) => safeRendererStyleDeclaration(propertyName, value, ""))
    .filter(Boolean);

  return safeDeclarations.length > 0 ? safeDeclarations.join(";") : fallback;
}

export function safeRendererPercentStyle(propertyName, value, fallback = "") {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return fallback;
  }

  const clampedValue = Math.max(0, Math.min(100, numericValue));
  const normalizedValue = Number(clampedValue.toFixed(3));
  return safeRendererStyleDeclaration(propertyName, `${normalizedValue}%`, fallback);
}

export function safeRendererAttributeValue(value, fallback = "") {
  const safeValue = String(value ?? "").trim();
  if (!safeValue) {
    return escapeHtml(String(fallback || ""));
  }

  return SAFE_ATTR_VALUE_RE.test(safeValue)
    ? escapeHtml(safeValue)
    : escapeHtml(String(fallback || ""));
}

export function safeRendererIntegerAttributeValue(value, fallback = "0") {
  const numericValue = Number(value);
  if (!Number.isInteger(numericValue)) {
    return safeRendererAttributeValue(fallback, "0");
  }

  return safeRendererAttributeValue(String(numericValue), "0");
}

export function renderDataAttributes(attributes = {}) {
  return Object.entries(attributes)
    .filter(([, value]) => value !== undefined && value !== null && String(value).trim() !== "")
    .map(([key, value]) => ` data-${key}="${safeRendererAttributeValue(value)}"`)
    .join("");
}

export function renderHtmlAttributes(attributes = {}) {
  return Object.entries(attributes)
    .map(([name, value]) => {
      const safeName = String(name || "").trim();
      if (!SAFE_HTML_ATTR_NAME_RE.test(safeName)) {
        return "";
      }
      if (value === false || value === null || value === undefined) {
        return "";
      }
      if (value === true) {
        return ` ${safeName}`;
      }
      return ` ${safeName}="${safeRendererAttributeValue(value)}"`;
    })
    .join("");
}
